export const BLOG_POSTS = [
  {
    slug: 'ai-got-good-before-it-got-dependable',
    title: 'AI got good before it got dependable',
    description:
      'Since GPT-3, models have become smarter, capable of longer stretches of useful work, and dramatically cheaper. The deeper story is how reasoning, agents, open weights, and specialist models changed what AI is.',
    eyebrow: 'Six years of AI, in three charts',
    publishedAt: '2026-09-25',
    displayDate: '25 September 2026',
    readTime: '10 min read',
    tags: ['AI', 'Agents', 'Open Models', 'Economics'],
  },
];

export const MMLU_DATA = [
  { name: 'GPT-3', date: '2020-05-28', value: 43.9 },
  { name: 'GPT-3.5 Turbo', date: '2023-03-01', value: 70.0 },
  { name: 'GPT-4', date: '2023-03-14', value: 86.4 },
  { name: 'Claude 3 Opus', date: '2024-03-04', value: 86.8 },
  { name: 'GPT-4o', date: '2024-05-13', value: 88.7 },
  { name: 'Claude 3.5 Sonnet', date: '2024-06-20', value: 88.7 },
  { name: 'o1-preview', date: '2024-09-12', value: 92.3 },
];

export const TASK_HORIZON_DATA = [
  { name: 'GPT-2', date: '2019-02-14', p50: 0.053778, p80: 0.0128 },
  { name: 'GPT-3', date: '2020-05-28', p50: 0.144057, p80: 0.056238 },
  { name: 'GPT-3.5 Turbo Instruct', date: '2022-03-15', p50: 0.599247, p80: 0.25538 },
  { name: 'GPT-4', date: '2023-03-14', p50: 3.987428, p80: 0.889561 },
  { name: 'GPT-4 Turbo', date: '2023-11-06', p50: 4.044959, p80: 0.783032 },
  { name: 'GPT-4o', date: '2024-05-13', p50: 6.991195, p80: 1.267009 },
  { name: 'Claude 3.5 Sonnet', date: '2024-06-20', p50: 11.395377, p80: 1.671757 },
  { name: 'o1-preview', date: '2024-09-12', p50: 20.326586, p80: 4.420545 },
  { name: 'o1', date: '2024-12-05', p50: 38.831588, p80: 7.090121 },
  { name: 'Claude 3.7 Sonnet', date: '2025-02-24', p50: 60.388937, p80: 12.09179 },
  { name: 'o3', date: '2025-04-16', p50: 119.732634, p80: 29.981603 },
  { name: 'GPT-5', date: '2025-08-07', p50: 203.012577, p80: 38.312431 },
  { name: 'Gemini 3 Pro', date: '2025-11-18', p50: 224.325884, p80: 54.142849 },
  { name: 'Claude Opus 4.5', date: '2025-11-24', p50: 292.994594, p80: 49.430584 },
  { name: 'GPT-5.2', date: '2025-12-11', p50: 352.249302, p80: 66.002649 },
  { name: 'Claude Opus 4.6', date: '2026-02-05', p50: 718.80683, p80: 69.874587 },
  { name: 'Claude Mythos preview', date: '2026-04-07', p50: 1044.780145, p80: 185.911829 },
];

export const CAPABILITY_COST_DATA = [
  { date: '2022-11-30', model35: 'GPT-3.5', gpt35: 20 },
  { date: '2023-03-06', model35: 'GPT-3.5 Turbo', gpt35: 2 },
  { date: '2023-03-14', model4: 'GPT-4', gpt4: 37.5 },
  { date: '2023-11-06', model35: 'GPT-3.5 Turbo 1106', gpt35: 1.25, model4: 'GPT-4 Turbo', gpt4: 15 },
  { date: '2024-01-25', model35: 'GPT-3.5 Turbo 0125', gpt35: 0.75 },
  { date: '2024-03-04', model35: 'Claude 3 Haiku', gpt35: 0.5 },
  { date: '2024-05-13', model4: 'GPT-4o', gpt4: 7.5 },
  { date: '2024-07-23', model35: 'Llama 3.1 8B hosted', gpt35: 0.1 },
  { date: '2024-10-01', model4: 'Gemini 1.5 Pro 002', gpt4: 2.19 },
  { date: '2024-10-03', model35: 'Gemini Flash 8B', gpt35: 0.07 },
  { date: '2025-02-05', model4: 'Gemini 2 Flash', gpt4: 0.18 },
];

export const CURRENT_MODELS = [
  { model: 'Claude Opus 5.5', score: 57.6, price: '$8.00', access: 'Closed', note: 'Highest measured overall' },
  { model: 'GPT-6 Astra', score: 52.7, price: '$20.00', access: 'Closed', note: 'Premium OpenAI reasoning' },
  { model: 'Muse Spark 1.3', score: 48.1, price: '$2.00', access: 'Closed', note: 'Strong price/performance' },
  { model: 'GPT-6 Sol', score: 47.5, price: '$4.00', access: 'Closed', note: 'Balanced OpenAI tier' },
  { model: 'MiMo-V2.6-Pro', score: 46.3, price: '$0.54', access: 'Open weights', note: 'Highest-scoring open weights' },
  { model: 'GLM-5.3 Flash', score: 41.8, price: '$0.24', access: 'Open weights', note: 'Very low-cost serving' },
  { model: 'GPT-6 Luna', score: 37.3, price: '$0.20', access: 'Closed', note: 'Lowest-cost current OpenAI tier' },
];

export const OPEN_WEIGHT_MILESTONES = [
  {
    year: '2022',
    model: 'BLOOM',
    detail: 'A public research collaboration released a 176B-parameter multilingual model—proof that a GPT-3-sized model could be trained outside one company.',
    meta: '176B · BigScience RAIL license',
  },
  {
    year: '2023',
    model: 'Llama 2 + Mixtral',
    detail: 'Llama built the distribution ecosystem; Mixtral made efficiency the story by activating only part of a much larger sparse model for each token.',
    meta: '7B–70B Llama · 12.9B active Mixtral · mixed licenses',
  },
  {
    year: '2024',
    model: 'Llama 3.1 + Qwen 2.5',
    detail: 'Open weights expanded in both directions: a 405B flagship at one end and compact, multilingual, coding, and mathematics specialists at the other.',
    meta: '0.5B–405B · mostly permissive or community licenses',
  },
  {
    year: '2025',
    model: 'DeepSeek-R1 + gpt-oss',
    detail: 'Reasoning traces, distillation, and configurable thinking moved beyond closed APIs. OpenAI returned to open weights with a 20B model for 16GB memory and a 120B model for one 80GB GPU.',
    meta: 'MIT and Apache 2.0 releases',
  },
  {
    year: '2026',
    model: 'Mistral Small 4',
    detail: 'The categories converged: one sparse model combined text and image input, coding, tool use, long context, and configurable reasoning.',
    meta: '119B total / about 6B active · Apache 2.0',
  },
];
